import React, { useState } from "react";
import { TextField } from "mui-rff";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@material-ui/icons/VisibilityOffOutlined";
import { useTranslate } from "react-polyglot";
import PropTypes from "prop-types";
import { makeStyles, Typography } from "@material-ui/core";
import clsx from "clsx";
import ReactFinalForm from "../../../SharedComponents/ReactFinalForm.jsx";
import { useAuth } from "../../../Providers/Auth/AuthProvider.jsx";
import useTitle from "../../../hooks/useTitle.js";
import FlexboxVertical from "../../../SharedComponents/FlexboxVertical.jsx";
import { useBaseData } from "../../../Providers/Data/BaseDataProvider.jsx";
import useCompanyPublicId from "../../../hooks/useCompanyPublicId.js";
import useFavicon from "../../../hooks/useFavicon.js";
// import { domains } from "../../../constants.js";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    maxWidth: 650,
  },
  title: {
    marginTop: 30,
    color: "#00213A",
    fontSize: 20,
    fontWeight: 500,
    lineHeight: "27px",
  },
  form: {
    width: "100%",
    marginTop: 40,
    backgroundColor: "transparent",
  },
  fieldLabel: {
    marginTop: 10,
    marginBottom: 5,
    color: "#54606B",
    fontSize: 16,
    lineHeight: "22px",
  },
  firstFieldLabel: {
    marginTop: 0,
  },
  textField: {
    width: "100%",
    margin: 0,
    backgroundColor: "#FAFBFB",
    borderRadius: 15,
    "& .MuiOutlinedInput-root": {
      minHeight: 52,
      color: "#00213A",
      backgroundColor: "#FAFBFB",
      borderRadius: 15,
      fontSize: 16,
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#909293",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderWidth: 1,
        borderColor: "#909293",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E2E4E5",
    },
    "& .MuiOutlinedInput-input": {
      boxSizing: "border-box",
      height: 52,
      padding: 15,
      caretColor: theme.palette.primary.main,
    },
    "& .MuiFormHelperText-root": {
      margin: "2px 0 0",
    },
  },
  visibilityButton: {
    padding: 8,
    color: "#54606B",
  },
  submitButton: {
    width: "100%",
    height: 52,
    margin: "20px 0",
    borderRadius: 12,
    backgroundColor: theme.palette.primary.main,
    textTransform: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      filter: "brightness(0.9)",
    },
    "& .MuiButton-label": {
      fontSize: 17,
      fontWeight: 400,
    },
  },
}));

/** @param {String} className */
const Registration = ({ className }) => {
  // const { companyName, domain } = useCompanyPublicId();
  const { companyName } = useCompanyPublicId();
  const classes = useStyles();
  const t = useTranslate();
  const { register, company2, checkEmailResponse } = useAuth();
  const { preselectedSubject, preselectedOffer } = useBaseData();
  const domain = window.location.host;
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const rawCompanyColor =
    (checkEmailResponse &&
      checkEmailResponse.company &&
      checkEmailResponse.company.colors &&
      checkEmailResponse.company.colors.main) ||
    (company2 && company2.colors && company2.colors.main);
  const companyColor = rawCompanyColor
    ? rawCompanyColor.charAt(0) === "#"
      ? rawCompanyColor
      : `#${rawCompanyColor}`
    : null;
  const nameLabel = domain !== "dev.docker.web.skillzrun.com" ? t("authRegistration.name") : t("firstLastName");
  const phoneLabel = domain !== "dev.docker.web.skillzrun.com" ? t("authRegistration.phone") : t("formatPhone");
  const passwordLabel = t("authRegistration.password");
  const confirmPasswordLabel = t("authRegistration.confirmPassword");
  const caretStyle = companyColor ? { caretColor: companyColor } : undefined;

  useTitle("title.registration", { school: (company2 && company2.name) || companyName || domain });

  useFavicon();

  /**
   * @param {String} name
   * @param {String} phone
   * @param {String} password
   * @param {String} aboutMe
   * */
  const onSubmit = ({ name, phone, aboutMe, password }) => {
    // Очищаем номер телефона от всех символов, кроме цифр
    phone = phone ? phone.replace(/\D/g, "") : "";
    if (aboutMe) {
      aboutMe = `${t("fullAge")}: ${aboutMe}`;
    }
    register(name, phone, aboutMe, password, preselectedSubject, preselectedOffer).then();
  };

  return (
    <FlexboxVertical className={classes.root}>
      <Typography className={classes.title} align="center">
        {t("authRegistration.title")}
      </Typography>
      <ReactFinalForm
        onSubmit={onSubmit}
        formClassName={clsx(classes.form, className)}
        validate={({ aboutMe, password, confirmPassword }) => {
          const errors = {};
          if (confirmPassword !== password) {
            errors.confirmPassword = t("passwordsMatch");
          }
          if (domain === "dev.docker.web.skillzrun.com" && aboutMe) {
            if ((!!parseInt(aboutMe) && parseInt(aboutMe) < 10) || parseInt(aboutMe) > 99) {
              errors.aboutMe = t("errorAge");
            } else if (!parseInt(aboutMe)) {
              errors.aboutMe = t("notNumber");
            }
          }
          return errors;
        }}
      >
        <FlexboxVertical>
          <Typography
            className={clsx(classes.fieldLabel, classes.firstFieldLabel)}
            component="label"
            htmlFor="registration-name"
          >
            {nameLabel}
          </Typography>
          <TextField
            className={classes.textField}
            id="registration-name"
            name="name"
            variant="outlined"
            fullWidth
            fieldProps={{ defaultValue: checkEmailResponse?.name }}
            inputProps={{ "aria-label": nameLabel, style: caretStyle }}
            required
          />
          <Typography className={classes.fieldLabel} component="label" htmlFor="registration-phone">
            {phoneLabel}
          </Typography>
          <TextField
            className={classes.textField}
            id="registration-phone"
            name="phone"
            variant="outlined"
            fullWidth
            fieldProps={{ defaultValue: "" }}
            inputProps={{ "aria-label": phoneLabel, style: caretStyle }}
          />
          {domain === "dev.docker.web.skillzrun.com" && (
            <>
              <Typography className={classes.fieldLabel} component="label" htmlFor="registration-about-me">
                {t("fullAge")}
              </Typography>
              <TextField
                className={classes.textField}
                id="registration-about-me"
                name="aboutMe"
                variant="outlined"
                fullWidth
                inputProps={{ "aria-label": t("fullAge"), style: caretStyle }}
                required
              />
            </>
          )}
          <Typography className={classes.fieldLabel} component="label" htmlFor="registration-password">
            {passwordLabel}
          </Typography>
          <TextField
            className={classes.textField}
            id="registration-password"
            name="password"
            type={isPasswordVisible ? "text" : "password"}
            variant="outlined"
            fullWidth
            inputProps={{ "aria-label": passwordLabel, style: caretStyle }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    className={classes.visibilityButton}
                    type="button"
                    aria-label={passwordLabel}
                    edge="end"
                    onClick={() => setPasswordVisible((visible) => !visible)}
                    onMouseDown={(event) => event.preventDefault()}
                  >
                    {isPasswordVisible ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            required
          />
          <Typography className={classes.fieldLabel} component="label" htmlFor="registration-confirm-password">
            {confirmPasswordLabel}
          </Typography>
          <TextField
            className={classes.textField}
            id="registration-confirm-password"
            name="confirmPassword"
            type={isConfirmPasswordVisible ? "text" : "password"}
            variant="outlined"
            fullWidth
            inputProps={{ "aria-label": confirmPasswordLabel, style: caretStyle }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    className={classes.visibilityButton}
                    type="button"
                    aria-label={confirmPasswordLabel}
                    edge="end"
                    onClick={() => setConfirmPasswordVisible((visible) => !visible)}
                    onMouseDown={(event) => event.preventDefault()}
                  >
                    {isConfirmPasswordVisible ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            required
          />
          <Button
            className={classes.submitButton}
            size="large"
            type="submit"
            variant="contained"
            color="primary"
            disableElevation
            style={companyColor ? { backgroundColor: companyColor } : undefined}
          >
            {t("registration")}
          </Button>
        </FlexboxVertical>
      </ReactFinalForm>
    </FlexboxVertical>
  );
};

Registration.propTypes = {
  className: PropTypes.string,
};

export default Registration;
